import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";

import { AnimatePresence, motion } from "framer-motion";
import Lottie from "react-lottie";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import * as poofAnimationData from "../assets/poof2.json";
import CustomEPG from "../components/EPG/CustomEPG";
import LeftMenu from "../components/LeftMenu";
import ManageCategories from "../components/ManageCategories";
import MobileMenu from "../components/MobileMenu";
import PlyrVideoPlayer from "../components/PlyrVideoPlayer";
import { initializePush } from "../config/push";
import apiService from "../services/api";
import "./WebPlayerLiveTV.css";
import { debugLog } from "../utils/debugLog";
import { activateOnEnterOrSpace } from "../utils/keyboardActivation";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerLiveTV = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const profile = useMemo(
    () =>
      location.state?.profile ||
      JSON.parse(sessionStorage.getItem("selectedProfile") || "{}"),
    [location.state?.profile]
  );
  const playlist = useMemo(
    () =>
      location.state?.playlist ||
      JSON.parse(localStorage.getItem("selectedPlaylist") || "{}"),
    [location.state?.playlist]
  );

  const [categories, setCategories] = useState([]);
  const [allCategories, setAllCategories] = useState([]); // Store all categories including hidden
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [channels, setChannels] = useState([]);
  const [epgData, setEpgData] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [showManageCategories, setShowManageCategories] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [currentStreamUrl, setCurrentStreamUrl] = useState(null);
  const [currentStreamTitle, setCurrentStreamTitle] = useState(null);
  const [showPoofAnimations, setShowPoofAnimations] = useState([]); // Array of poof positions
  const [enabledCategoryIds, setEnabledCategoryIds] = useState([]); // Changed to array for multiple
  const [showChannelSorting, setShowChannelSorting] = useState(false);
  const [sortingCategory, setSortingCategory] = useState(null);
  const [categorySortSettings, setCategorySortSettings] = useState(() => {
    const saved = localStorage.getItem("channel_sort_settings");

    return saved ? JSON.parse(saved) : {};
  });
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);

  // Reminder states
  const [showProgramDetails, setShowProgramDetails] = useState(false);
  const [selectedProgramForReminder, setSelectedProgramForReminder] =
    useState(null);
  const [showReminderTimeSelect, setShowReminderTimeSelect] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [showCancelReminderDialog, setShowCancelReminderDialog] =
    useState(false);
  const [reminderToCancel, setReminderToCancel] = useState(null);
  const [pushSubscription, setPushSubscription] = useState(null);
  const [countdownTick, setCountdownTick] = useState(Date.now()); // For live countdown updates
  const programLongPressTriggeredRef = useRef(false);
  const selectedCategoryRef = useRef(null); // Ref to track current category for event handlers
  const loadingCategoryRef = useRef(null); // Ref to prevent duplicate loadChannels calls
  const loadCategoriesRef = useRef(null);
  const loadChannelsRef = useRef(null);
  const loadFavoriteChannelsRef = useRef(null);
  const loadRemindersViewRef = useRef(null);

  // Update current time every 30 seconds for better accuracy
  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(new Date());
    };

    updateTime();
    const interval = setInterval(updateTime, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Keep ref in sync with selectedCategory for event handlers
  useEffect(() => {
    selectedCategoryRef.current = selectedCategory;
  }, [selectedCategory]);

  // Keep reminders category count in sync with actual reminders
  useEffect(() => {
    // Filter to only count valid (non-expired) reminders
    const now = Date.now();
    const validCount = reminders.filter((r) => {
      const programStart = new Date(r.programStart).getTime();
      const reminderTime =
        typeof r.reminderTime === "string"
          ? new Date(r.reminderTime).getTime()
          : r.reminderTime;

      return programStart > now && reminderTime > now;
    }).length;

    // Update the category count
    setCategories((prevCategories) => {
      const hasRemindersCategory = prevCategories.some(
        (cat) => cat.id === "reminders"
      );

      if (validCount > 0 && !hasRemindersCategory) {
        // Add reminders category after favorites
        const favoritesIndex = prevCategories.findIndex(
          (cat) => cat.id === "favorites"
        );
        const newCategories = [...prevCategories];

        newCategories.splice(favoritesIndex + 1, 0, {
          id: "reminders",
          name: "Reminders",
          icon: "🔔",
          count: validCount,
          isFixed: true,
        });

        return newCategories;
      } else if (validCount === 0 && hasRemindersCategory) {
        // Remove reminders category
        return prevCategories.filter((cat) => cat.id !== "reminders");
      } else if (hasRemindersCategory) {
        // Update count
        return prevCategories.map((cat) =>
          cat.id === "reminders" ? { ...cat, count: validCount } : cat
        );
      }

      return prevCategories;
    });
  }, [reminders]);

  // Live countdown timer for reminders view
  useEffect(() => {
    if (selectedCategory !== "reminders") return;

    const interval = setInterval(() => {
      setCountdownTick(Date.now());
    }, 1000); // Update every second

    return () => clearInterval(interval);
  }, [selectedCategory]);

  // Check for profile and redirect if not present
  useEffect(() => {
    if (!profile || !profile.id) {
      navigate("/web-player/profiles");

      return;
    }

    // Check for webPlayerToken
    const webPlayerToken = webPlayerStorage.getToken();

    if (!webPlayerToken) {
      navigate("/web-player");

      return;
    }

    loadCategoriesRef.current?.();
  }, [profile, navigate]);

  // Handle "go to channel" navigation from reminder notification (runs once on mount)
  useEffect(() => {
    const goToChannel = sessionStorage.getItem("goToChannel");

    if (goToChannel) {
      try {
        const channelData = JSON.parse(goToChannel);

        debugLog("[LiveTV] Go to channel:", channelData);
        // Clear immediately to prevent re-runs
        sessionStorage.removeItem("goToChannel");
        // Navigate to the category after a small delay
        if (channelData.categoryId) {
          setTimeout(() => {
            setSelectedCategory(channelData.categoryId);
          }, 100);
        }
      } catch (e) {
        console.error("Error parsing goToChannel:", e);
        sessionStorage.removeItem("goToChannel");
      }
    }
  }, []); // Empty dependency - only runs on mount

  const loadCategories = async () => {
    try {
      // Load live TV categories from real API
      const response = await apiService.webPlayerGetLiveTVCategories();

      const categoriesArray = response?.data || response;

      // Also get bookmarks to know the favorites count
      const bookmarksResponse = await apiService.webPlayerGetBookmarks();
      const liveTvBookmarks = bookmarksResponse?.data?.liveTv || [];
      const favoritesCount = liveTvBookmarks.length;

      // Get reminders count
      let remindersCount = 0;

      try {
        const remindersResponse = await apiService.webPlayerGetReminders();
        const remindersData = remindersResponse?.data || [];
        // Only count future reminders
        const now = Date.now();

        remindersCount = remindersData.filter(
          (r) => new Date(r.programStart).getTime() > now
        ).length;
      } catch (err) {
        // Fallback to localStorage
        const saved = localStorage.getItem("live_tv_reminders");

        if (saved) {
          const localReminders = JSON.parse(saved);
          const now = Date.now();

          remindersCount = localReminders.filter(
            (r) => new Date(r.programStart).getTime() > now
          ).length;
        }
      }

      if (Array.isArray(categoriesArray)) {
        // Android app uses itemCount field for channel counts
        const categoriesData = categoriesArray.map((cat) => ({
          id: cat.categoryId || cat.id,
          name: cat.categoryName || cat.name,
          // Android app uses itemCount for the channel count
          count: cat.itemCount || 0,
        }));

        // Store all categories for manage dialog
        setAllCategories(categoriesData);

        // Check if profile has saved settings
        const profileSettings = profile?.preferences?.liveTvSettings;
        let visibleCategories;
        let orderedCategories;

        if (profileSettings?.categoryVisibility) {
          // Use profile settings
          visibleCategories = categoriesData.filter(
            (cat) => profileSettings.categoryVisibility[cat.id] !== false
          );

          // Apply profile's custom order
          if (profileSettings.categoryOrder) {
            const order = profileSettings.categoryOrder;

            orderedCategories = order
              .map((id) => visibleCategories.find((cat) => cat.id === id))
              .filter(Boolean)
              .concat(
                visibleCategories.filter((cat) => !order.includes(cat.id))
              );
          } else {
            orderedCategories = visibleCategories;
          }
        } else {
          // Fall back to localStorage
          // Filter hidden categories
          visibleCategories = categoriesData.filter((cat) => {
            const isHidden =
              localStorage.getItem(`category_hidden_${cat.id}`) === "true";

            return !isHidden;
          });

          // Apply custom order from localStorage
          const savedOrder = localStorage.getItem("category_order");

          if (savedOrder) {
            const order = JSON.parse(savedOrder);

            orderedCategories = order
              .map((id) => visibleCategories.find((cat) => cat.id === id))
              .filter(Boolean)
              .concat(
                visibleCategories.filter((cat) => !order.includes(cat.id))
              );
          } else {
            orderedCategories = visibleCategories;
          }
        }

        // Add fixed categories at the beginning
        const fixedCategories = [
          {
            id: "manage",
            name: "Manage Categories",
            icon: "fa-solid fa-bars",
            count: 0,
            isFixed: true,
          },
          {
            id: "multiview",
            name: "MultiView",
            icon: "fa-solid fa-grid-2-plus",
            count: 0,
            isFixed: true,
          },
          {
            id: "favorites",
            name: "Favorites",
            icon: "fa-solid fa-star",
            count: favoritesCount,
            isFixed: true,
          },
        ];

        // Add reminders category if there are any
        if (remindersCount > 0) {
          fixedCategories.push({
            id: "reminders",
            name: "Reminders",
            icon: "🔔",
            count: remindersCount,
            isFixed: true,
          });
        }

        const allCategories = [...fixedCategories, ...orderedCategories];

        setCategories(allCategories);

        // Select first regular category (after favorites)
        if (categoriesData.length > 0) {
          setSelectedCategory(categoriesData[0].id);
          loadChannels(categoriesData[0].id);
        }
      }
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  };

  loadCategoriesRef.current = loadCategories;

  const loadChannels = async (categoryId = null) => {
    // Guard against duplicate/concurrent calls for the same category
    if (loadingCategoryRef.current === categoryId) {
      debugLog(
        `[loadChannels] Skipping duplicate call for category: ${categoryId}`
      );

      return;
    }
    loadingCategoryRef.current = categoryId;

    try {
      setLoading(true);
      debugLog(`Loading channels for category: ${categoryId || "all"}`);

      const params = {
        page: 1,
        limit: 100,
      };

      if (categoryId) {
        params.categories = categoryId;
      }

      const response = await apiService.webPlayerGetLiveTV(params);

      // Get bookmarks to check which channels are favorites
      debugLog("Fetching bookmarks to identify favorites...");
      const bookmarksResponse = await apiService.webPlayerGetBookmarks();

      // The API returns { status: 200, data: { liveTv: [...], movies: [...], series: [...] } }
      const liveTvBookmarks =
        bookmarksResponse?.data?.liveTv || bookmarksResponse?.liveTv || [];

      debugLog(`Found ${liveTvBookmarks.length} live-tv bookmarks`);

      // Create set of bookmarked live-tv IDs
      const bookmarkedIds = new Set();

      liveTvBookmarks.forEach((bookmark) => {
        const id = bookmark.mediaId?.toString();

        if (id) {
          bookmarkedIds.add(id);
        }
      });

      debugLog("Live-TV bookmarked IDs:", Array.from(bookmarkedIds));

      // Handle different response formats
      const channelsArray = response?.data || response?.items || response;

      debugLog(`Found ${channelsArray?.length || 0} channels`);

      if (Array.isArray(channelsArray)) {
        const channelsData = channelsArray.map((channel, index) => {
          const channelId =
            channel.mediaId?.toString() ||
            channel.id?.toString() ||
            channel.stream_id?.toString();
          const isBookmarked = bookmarkedIds.has(channelId);

          if (isBookmarked) {
            debugLog(
              `Channel "${channel.name}" (ID: ${channelId}) is bookmarked`
            );
          }

          return {
            uuid: channelId || `channel-${index}`,
            title: channel.name,
            logo:
              channel.logoPath ||
              channel.posterPath ||
              channel.logo ||
              channel.stream_icon,
            streamUrl: channel.streamUrl || channel.stream_url,
            epgChannelId: channel.epgChannelId || channel.epg_channel_id,
            categoryId: channel.categoryId || channel.category_id,
            programmes: channel.programmes || [],
            isBookmarked,
          };
        });

        setChannels(channelsData);
        prepareEpgData(channelsData);

        // Update favorites count in categories
        const liveTvFavoritesCount = Array.from(bookmarkedIds).length;

        debugLog(`Total live-tv favorites count: ${liveTvFavoritesCount}`);

        setCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat.id === "favorites"
              ? { ...cat, count: liveTvFavoritesCount }
              : cat
          )
        );
      } else {
        debugLog("Unexpected response format:", response);
        setChannels([]);
        setEpgData([]);
      }
    } catch (error) {
      console.error("Error loading channels:", error);
      setChannels([]);
      setEpgData([]);
    } finally {
      setLoading(false);
      // Clear the loading ref after a short delay to allow for intentional reloads
      setTimeout(() => {
        loadingCategoryRef.current = null;
      }, 500);
    }
  };

  loadChannelsRef.current = loadChannels;

  const prepareEpgData = (channelsData) => {
    // Transform programmes for EPG format
    const allPrograms = [];

    channelsData.forEach((channel) => {
      if (channel.programmes && channel.programmes.length > 0) {
        channel.programmes.forEach((program) => {
          allPrograms.push({
            channelUuid: channel.uuid,
            id: `${channel.uuid}-${program.start}`,
            title: program.title || "No Title",
            description: program.description || "",
            since: new Date(program.start).toISOString(),
            till: new Date(program.stop).toISOString(),
            image: program.backdropPath || channel.logo || "",
          });
        });
      } else {
        // Create placeholder programs for channels without EPG data
        const now = new Date();
        const dayStart = new Date(now);

        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(now);

        dayEnd.setHours(23, 59, 59, 999);

        allPrograms.push({
          channelUuid: channel.uuid,
          id: `${channel.uuid}-placeholder`,
          title: channel.title || "No Programming Information",
          description: "",
          since: dayStart.toISOString(),
          till: dayEnd.toISOString(),
          image: channel.logo || "",
        });
      }
    });

    setEpgData(allPrograms);
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategory(categoryId);

    // Handle special fixed categories
    if (categoryId === "manage") {
      setShowManageCategories(true);

      return;
    } else if (categoryId === "multiview") {
      // Navigate to MultiView page
      navigate("/web-player/multiview", {
        state: { profile, playlist },
      });

      return;
    } else if (categoryId === "favorites") {
      // Load favorite channels from API
      loadFavoriteChannels();
    } else if (categoryId === "reminders") {
      // Load reminders view
      loadRemindersView();
    } else {
      // Load channels based on category
      loadChannels(categoryId);
    }
  };

  const loadFavoriteChannels = async () => {
    try {
      setLoading(true);
      debugLog("Loading favorite channels...");

      // First, get all bookmarked items from /v2/bookmarks
      const bookmarksResponse = await apiService.webPlayerGetBookmarks();

      debugLog("Bookmarks response:", bookmarksResponse);

      // The API returns { status: 200, data: { liveTv: [...], movies: [...], series: [...] } }
      // We only want the liveTv bookmarks
      let liveTvBookmarks = [];

      if (
        bookmarksResponse?.data?.liveTv &&
        Array.isArray(bookmarksResponse.data.liveTv)
      ) {
        // This is the expected structure
        liveTvBookmarks = bookmarksResponse.data.liveTv;
        debugLog(
          `Found ${liveTvBookmarks.length} live-tv bookmarks from data.liveTv`
        );
      } else if (
        bookmarksResponse?.liveTv &&
        Array.isArray(bookmarksResponse.liveTv)
      ) {
        // Direct liveTv property
        liveTvBookmarks = bookmarksResponse.liveTv;
        debugLog(
          `Found ${liveTvBookmarks.length} live-tv bookmarks from liveTv`
        );
      } else {
        debugLog("No live-tv bookmarks found in response:", bookmarksResponse);
      }

      debugLog("Live-TV bookmarks:", liveTvBookmarks);

      if (liveTvBookmarks.length === 0) {
        debugLog("No live-tv favorites found");
        // No live-tv favorites
        setChannels([]);
        setEpgData([]);
        setLoading(false);

        return;
      }

      // The liveTvBookmarks already contain all the channel information we need!
      // No need to fetch all channels and match them
      debugLog(`Processing ${liveTvBookmarks.length} favorite channels`);

      const channelsData = liveTvBookmarks.map((channel, index) => ({
        uuid: channel.mediaId?.toString() || `channel-${index}`,
        title: channel.name,
        logo: channel.logoPath || channel.posterPath || channel.logo,
        streamUrl: channel.streamUrl,
        epgChannelId: channel.epgChannelId,
        categoryId: channel.categoryId,
        programmes: channel.programmes || [],
        isBookmarked: true, // These are all bookmarked by definition
        // Include other useful fields from the bookmark data
        tmdbId: channel.tmdbId,
        year: channel.year,
        genres: channel.genres,
        rating: channel.rating,
        backdropPath: channel.backdropPath,
      }));

      debugLog("Prepared channels data for EPG:", channelsData);
      setChannels(channelsData);
      prepareEpgData(channelsData);

      // Update favorites count in categories
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === "favorites" ? { ...cat, count: channelsData.length } : cat
        )
      );
    } catch (error) {
      console.error("Error loading favorite channels:", error);
      // Show empty favorites on error
      setChannels([]);
      setEpgData([]);
    } finally {
      setLoading(false);
    }
  };

  loadFavoriteChannelsRef.current = loadFavoriteChannels;

  const handleChannelSelect = (channel) => {
    setSelectedChannel(channel);
    if (channel?.streamUrl) {
      // Decode base64 stream URL
      let decodedUrl = channel.streamUrl;

      try {
        // Check if it's base64 encoded (doesn't start with http)
        if (!channel.streamUrl.startsWith("http")) {
          decodedUrl = atob(channel.streamUrl);
        }
        debugLog("Selected channel:", channel.title);
        debugLog("Decoded stream URL:", decodedUrl);

        // Store the decoded URL for video player
        const channelWithDecodedUrl = {
          ...channel,
          decodedStreamUrl: decodedUrl,
        };

        setSelectedChannel(channelWithDecodedUrl);

        // Launch video player with decoded URL
        setCurrentStreamUrl(decodedUrl);
        setCurrentStreamTitle(channel.title);
        setPlayerOpen(true);
      } catch (error) {
        console.error("Error decoding stream URL:", error);
        debugLog("Using original URL:", channel.streamUrl);
      }
    }
  };

  const handleProgramSelect = (program) => {
    setSelectedProgram(program);
    if (program.channel?.streamUrl) {
      // Decode base64 stream URL
      let decodedUrl = program.channel.streamUrl;

      try {
        // Check if it's base64 encoded (doesn't start with http)
        if (!program.channel.streamUrl.startsWith("http")) {
          decodedUrl = atob(program.channel.streamUrl);
        }
        debugLog(
          "Selected program:",
          program.title,
          "on",
          program.channel.title
        );
        debugLog("Decoded stream URL:", decodedUrl);

        // Store the decoded URL for video player
        const programWithDecodedUrl = {
          ...program,
          channel: {
            ...program.channel,
            decodedStreamUrl: decodedUrl,
          },
        };

        setSelectedProgram(programWithDecodedUrl);

        // Launch video player with decoded URL
        setCurrentStreamUrl(decodedUrl);
        setCurrentStreamTitle(`${program.title} - ${program.channel.title}`);
        setPlayerOpen(true);
      } catch (error) {
        console.error("Error decoding stream URL:", error);
        debugLog("Using original URL:", program.channel.streamUrl);
      }
    }
  };

  const toggleFavorite = async (channel) => {
    try {
      const mediaId = channel.uuid || channel.mediaId;
      const wasBookmarked = channel.isBookmarked;

      // Optimistically update UI immediately for better UX
      if (wasBookmarked) {
        // Update channels immediately
        setChannels((prevChannels) =>
          prevChannels.map((ch) =>
            ch.uuid === channel.uuid ? { ...ch, isBookmarked: false } : ch
          )
        );

        // Update favorites count immediately - decrement
        setCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat.id === "favorites"
              ? { ...cat, count: Math.max(0, (cat.count || 0) - 1) }
              : cat
          )
        );

        // If we're in favorites view, remove the channel
        if (selectedCategory === "favorites") {
          setTimeout(() => {
            setChannels((prevChannels) =>
              prevChannels.filter((ch) => ch.uuid !== channel.uuid)
            );
          }, 300); // Small delay for animation
        }
      } else {
        // Update channels immediately
        setChannels((prevChannels) =>
          prevChannels.map((ch) =>
            ch.uuid === channel.uuid ? { ...ch, isBookmarked: true } : ch
          )
        );

        // Update favorites count immediately - increment
        setCategories((prevCategories) =>
          prevCategories.map((cat) =>
            cat.id === "favorites"
              ? { ...cat, count: (cat.count || 0) + 1 }
              : cat
          )
        );
      }

      // Make API call
      if (wasBookmarked) {
        await apiService.deleteBookmark(null, mediaId, "live-tv");
      } else {
        await apiService.createBookmark(null, mediaId, "live-tv");
      }

      // Save to profile preferences if profile exists
      if (profile && profile.id) {
        try {
          // Get current bookmarks to save to profile
          const bookmarksResponse = await apiService.webPlayerGetBookmarks();
          const liveTvBookmarks = bookmarksResponse?.data?.liveTv || [];

          const updatedProfile = {
            ...profile,
            preferences: {
              ...(profile.preferences || {}),
              liveTvFavorites: liveTvBookmarks.map((b) => b.mediaId),
            },
          };

          await apiService.webPlayerUpdateProfile(profile.id, updatedProfile);
          debugLog("Saved favorites to profile");
        } catch (profileError) {
          console.error("Error saving favorites to profile:", profileError);
          // Don't fail the whole operation if profile save fails
        }
      }

      // Verify with server (in case of issues, correct the count)
      const bookmarksResponse = await apiService.webPlayerGetBookmarks();
      const liveTvBookmarks = bookmarksResponse?.data?.liveTv || [];
      const actualCount = liveTvBookmarks.length;

      // Update with actual count from server
      setCategories((prevCategories) =>
        prevCategories.map((cat) =>
          cat.id === "favorites" ? { ...cat, count: actualCount } : cat
        )
      );
    } catch (error) {
      console.error("Error toggling favorite:", error);

      // Revert optimistic update on error
      setChannels((prevChannels) =>
        prevChannels.map((ch) =>
          ch.uuid === channel.uuid
            ? { ...ch, isBookmarked: channel.isBookmarked }
            : ch
        )
      );
    }
  };

  // Long press handlers for channel sorting
  const handleCategoryMouseDown = (category) => {
    // Don't allow sorting on fixed categories
    if (category.isFixed) return;

    longPressTriggeredRef.current = false;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      // Vibrate if supported
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      setSortingCategory(category);
      setShowChannelSorting(true);
    }, 500); // 500ms hold time
  };

  const handleCategoryMouseUp = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleCategoryMouseLeave = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleSortOptionSelect = (sortType) => {
    if (!sortingCategory) return;

    const categoryId = sortingCategory.id;

    // Update sort settings
    const newSettings = { ...categorySortSettings };

    if (sortType === "reset") {
      delete newSettings[categoryId];
    } else {
      newSettings[categoryId] = sortType;
    }

    setCategorySortSettings(newSettings);
    localStorage.setItem("channel_sort_settings", JSON.stringify(newSettings));

    // Apply sorting to current channels
    applySortToChannels(sortType);

    // Close the modal
    setShowChannelSorting(false);
    setSortingCategory(null);
  };

  const applySortToChannels = (sortType) => {
    setChannels((prevChannels) => {
      const channelsCopy = [...prevChannels];

      if (sortType === "alphabetical") {
        channelsCopy.sort((a, b) =>
          (a.title || "").localeCompare(b.title || "")
        );
      } else if (sortType === "adaptive") {
        // Adaptive sort - could be based on watch history, but for now just keep current order
        // This would typically be the default API order or sorted by popularity/watch time
      } else if (sortType === "reset") {
        // Reset just reloads from API
        if (selectedCategory && selectedCategory !== "favorites") {
          loadChannels(selectedCategory);
        } else if (selectedCategory === "favorites") {
          loadFavoriteChannels();
        }

        return prevChannels;
      }

      return channelsCopy;
    });
  };

  // Get current sort setting for a category
  const getCurrentSortSetting = (categoryId) =>
    categorySortSettings[categoryId] || "adaptive";

  // ==================== REMINDER FUNCTIONS ====================

  // Request notification permission
  const requestNotificationPermission = async () => {
    if (!("Notification" in window)) {
      debugLog("This browser does not support notifications");

      return false;
    }

    if (Notification.permission === "granted") {
      return true;
    }

    if (Notification.permission !== "denied") {
      const permission = await Notification.requestPermission();

      return permission === "granted";
    }

    return false;
  };

  // Handle long press on program in EPG (called by CustomEPG when long press is detected)
  const handleProgramLongPress = (program, channel) => {
    programLongPressTriggeredRef.current = true;
    if (navigator.vibrate) {
      navigator.vibrate(50);
    }
    setSelectedProgramForReminder({
      ...program,
      channel,
      channelName: channel.title,
      channelLogo: channel.logo,
    });
    setShowProgramDetails(true);
  };

  // Format time for display
  const formatTime = (dateString) => {
    const date = new Date(dateString);

    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);

    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // Calculate duration
  const calculateDuration = (since, till) => {
    const start = new Date(since).getTime();
    const end = new Date(till).getTime();
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }

    return `${minutes}m`;
  };

  // Handle remind me button click
  const handleRemindMeClick = () => {
    setShowReminderTimeSelect(true);
  };

  // Handle reminder time selection
  const handleSetReminder = async (minutesBefore) => {
    const hasPermission = await requestNotificationPermission();

    if (!hasPermission) {
      toast.error(
        "Please enable notifications in your browser settings to receive reminders."
      );

      return;
    }

    const program = selectedProgramForReminder;
    const programStart = new Date(program.since).getTime();
    const reminderTime = programStart - minutesBefore * 60 * 1000; // Convert minutes to milliseconds
    const now = Date.now();

    // Check if reminder time has already passed
    if (reminderTime <= now) {
      const timeUntilStart = Math.floor((programStart - now) / (60 * 1000));

      toast.error(
        timeUntilStart > 0
          ? `Can't set ${minutesBefore} min reminder - only ${timeUntilStart} min until program starts. Try a shorter time.`
          : "Program is about to start or has already started.",
        { position: "top-center", autoClose: 5000 }
      );
      setShowReminderTimeSelect(false);
      setShowProgramDetails(false);
      setSelectedProgramForReminder(null);

      return;
    }

    // Get streamUrl from channel object
    const channelStreamUrl = program.channel?.streamUrl || program.streamUrl;

    const reminderData = {
      programId: program.id,
      programTitle: program.title,
      programDescription: program.description,
      programImage: program.image,
      channelId: program.channelUuid || program.channel?.uuid,
      channelName: program.channelName || program.channel?.title,
      channelLogo: program.channelLogo || program.channel?.logo,
      streamUrl: channelStreamUrl,
      categoryId: selectedCategory, // Store category for navigation
      programStart: program.since,
      programEnd: program.till,
      reminderTime: new Date(reminderTime).toISOString(),
      minutesBefore,
      pushSubscription,
    };

    try {
      // Try to save to API
      const response = await apiService.webPlayerCreateReminder(reminderData);

      const reminder = {
        id:
          response.data?.id ||
          `${program.channelUuid}-${program.id}-${Date.now()}`,
        ...reminderData,
        reminderTime, // Keep as timestamp for local scheduling
        createdAt: Date.now(),
      };

      // Add to local state
      const updatedReminders = [...reminders, reminder];

      setReminders(updatedReminders);

      // Also save to localStorage as backup
      localStorage.setItem(
        "live_tv_reminders",
        JSON.stringify(updatedReminders)
      );

      // Dispatch event so global ReminderManager can schedule it
      window.dispatchEvent(
        new CustomEvent("reminderAdded", { detail: reminder })
      );

      // Update categories to show Reminders
      updateRemindersCategory(updatedReminders.length);
    } catch (error) {
      console.error("Failed to save reminder to API:", error);

      // Fallback to local-only reminder
      const reminder = {
        id: `${program.channelUuid}-${program.id}-${Date.now()}`,
        ...reminderData,
        reminderTime,
        createdAt: Date.now(),
      };

      const updatedReminders = [...reminders, reminder];

      setReminders(updatedReminders);
      localStorage.setItem(
        "live_tv_reminders",
        JSON.stringify(updatedReminders)
      );

      // Dispatch event so global ReminderManager can schedule it
      window.dispatchEvent(
        new CustomEvent("reminderAdded", { detail: reminder })
      );

      updateRemindersCategory(updatedReminders.length);
    }

    // Close overlays
    setShowReminderTimeSelect(false);
    setShowProgramDetails(false);
    setSelectedProgramForReminder(null);
  };

  // Remove a reminder
  const removeReminder = async (reminderId) => {
    // Remove from state
    const updatedReminders = reminders.filter((r) => r.id !== reminderId);

    setReminders(updatedReminders);
    localStorage.setItem("live_tv_reminders", JSON.stringify(updatedReminders));

    // Update category count
    updateRemindersCategory(updatedReminders.length);

    // Try to delete from API
    try {
      await apiService.webPlayerDeleteReminder(reminderId);
    } catch (error) {
      console.error("Failed to delete reminder from API:", error);
      // Already removed locally, so just log the error
    }
  };

  // Update reminders category in sidebar
  const updateRemindersCategory = (count) => {
    setCategories((prevCategories) => {
      // Check if Reminders category already exists
      const hasReminders = prevCategories.some((cat) => cat.id === "reminders");

      if (count > 0 && !hasReminders) {
        // Add Reminders category after Favorites
        const favoritesIndex = prevCategories.findIndex(
          (cat) => cat.id === "favorites"
        );
        const newCategories = [...prevCategories];

        newCategories.splice(favoritesIndex + 1, 0, {
          id: "reminders",
          name: "Reminders",
          icon: "🔔",
          count,
          isFixed: true,
        });

        return newCategories;
      } else if (count === 0 && hasReminders) {
        // Remove Reminders category
        return prevCategories.filter((cat) => cat.id !== "reminders");
      } else if (hasReminders) {
        // Update count
        return prevCategories.map((cat) =>
          cat.id === "reminders" ? { ...cat, count } : cat
        );
      }

      return prevCategories;
    });
  };

  // Handle clicking on reminders category
  const loadRemindersView = () => {
    // Filter out expired reminders (program started or reminder time passed)
    const now = Date.now();
    const validReminders = reminders.filter((r) => {
      const programStart = new Date(r.programStart).getTime();
      const reminderTime =
        typeof r.reminderTime === "string"
          ? new Date(r.reminderTime).getTime()
          : r.reminderTime;

      // Keep if program hasn't started AND reminder hasn't fired yet
      return programStart > now && reminderTime > now;
    });

    if (validReminders.length !== reminders.length) {
      setReminders(validReminders);
      localStorage.setItem("live_tv_reminders", JSON.stringify(validReminders));
      updateRemindersCategory(validReminders.length);
    }

    // Sort reminders by reminder time (soonest first)
    const sortedReminders = [...validReminders].sort((a, b) => {
      const timeA =
        typeof a.reminderTime === "string"
          ? new Date(a.reminderTime).getTime()
          : a.reminderTime;
      const timeB =
        typeof b.reminderTime === "string"
          ? new Date(b.reminderTime).getTime()
          : b.reminderTime;

      return timeA - timeB;
    });

    // Create channel-like items from reminders for display
    const reminderChannels = sortedReminders.map((reminder) => ({
      uuid: reminder.id,
      title: reminder.programTitle,
      logo: reminder.channelLogo,
      channelName: reminder.channelName,
      since: reminder.programStart,
      till: reminder.programEnd,
      image: reminder.programImage,
      isReminder: true,
      reminderData: reminder,
    }));

    setChannels(reminderChannels);
    setEpgData([]);
    setLoading(false);
  };

  loadRemindersViewRef.current = loadRemindersView;

  // Handle reminder item click (show cancel dialog)
  const handleReminderClick = (reminder) => {
    setReminderToCancel(reminder);
    setShowCancelReminderDialog(true);
  };

  // Confirm cancel reminder
  const confirmCancelReminder = () => {
    if (reminderToCancel) {
      const reminderId =
        reminderToCancel.reminderData?.id || reminderToCancel.uuid;

      // Remove from reminders state and localStorage
      removeReminder(reminderId);

      // Immediately remove from channels list if in reminders view
      if (selectedCategory === "reminders") {
        setChannels((prevChannels) =>
          prevChannels.filter((ch) => ch.uuid !== reminderId)
        );
      }
    }
    setShowCancelReminderDialog(false);
    setReminderToCancel(null);
  };

  // Load reminders from API
  const loadRemindersFromAPI = useCallback(async () => {
    try {
      const response = await apiService.webPlayerGetReminders();
      const apiReminders = response.data || [];

      // Convert API format to local format
      const formattedReminders = apiReminders.map((r) => ({
        ...r,
        reminderTime:
          typeof r.reminderTime === "string"
            ? new Date(r.reminderTime).getTime()
            : r.reminderTime,
      }));

      setReminders(formattedReminders);
      localStorage.setItem(
        "live_tv_reminders",
        JSON.stringify(formattedReminders)
      );

      // Update category if there are reminders
      if (formattedReminders.length > 0) {
        updateRemindersCategory(formattedReminders.length);
      }
    } catch (error) {
      console.error("Failed to load reminders from API:", error);

      // Fallback to localStorage
      const saved = localStorage.getItem("live_tv_reminders");
      const localReminders = saved ? JSON.parse(saved) : [];

      setReminders(localReminders);

      if (localReminders.length > 0) {
        updateRemindersCategory(localReminders.length);
      }
    } finally {
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize Push notifications and reminders on mount
  useEffect(() => {
    // Initialize Push
    const initPush = async () => {
      try {
        const subscription = await initializePush();

        if (subscription) {
          setPushSubscription(subscription);

          // Register subscription with backend
          try {
            await apiService.webPlayerRegisterPushSubscription(subscription);
            debugLog("Push subscription registered with backend");
          } catch (error) {
            console.error("Failed to register push subscription:", error);
          }
        }
      } catch (error) {
        console.error("Failed to initialize push:", error);
      }
    };

    initPush();
    loadRemindersFromAPI();
  }, [loadRemindersFromAPI]);

  // Handle goToCategory event - separate useEffect to avoid dependency cycle
  useEffect(() => {
    let isHandling = false; // Prevent duplicate handling

    const handleGoToCategory = (event) => {
      const { categoryId } = event.detail;

      debugLog("[LiveTV] goToCategory event received:", categoryId);

      // Skip if already handling or same category
      if (isHandling) {
        debugLog("[LiveTV] Already handling goToCategory, skipping");

        return;
      }

      if (!categoryId || categoryId === selectedCategoryRef.current) {
        debugLog("[LiveTV] Same category or no categoryId, skipping");

        return;
      }

      isHandling = true;
      debugLog("[LiveTV] Changing to category:", categoryId);

      // Just set the category - let the normal UI flow handle channel loading
      setSelectedCategory(categoryId);
      selectedCategoryRef.current = categoryId;

      // Load content for non-special categories
      if (categoryId === "favorites") {
        loadFavoriteChannelsRef.current?.();
      } else if (categoryId === "reminders") {
        loadRemindersViewRef.current?.();
      } else if (categoryId !== "manage" && categoryId !== "multiview") {
        loadChannelsRef.current?.(categoryId);
      }

      // Reset handling flag after a delay
      setTimeout(() => {
        isHandling = false;
      }, 1000);
    };

    window.addEventListener("goToCategory", handleGoToCategory);
    debugLog("[LiveTV] goToCategory listener attached");

    return () => {
      window.removeEventListener("goToCategory", handleGoToCategory);
    };
  }, []); // Empty deps - uses ref for current category

  // Handle reminder channel auto-play
  useEffect(() => {
    const playChannel = (reminder) => {
      if (!reminder) return;

      debugLog("[LiveTV] Playing reminder channel:", reminder);

      // Try to play the channel
      if (reminder.streamUrl) {
        let decodedUrl = reminder.streamUrl;

        try {
          if (!reminder.streamUrl.startsWith("http")) {
            decodedUrl = atob(reminder.streamUrl);
          }
        } catch (e) {
          console.error("Error decoding stream URL:", e);
        }

        setCurrentStreamUrl(decodedUrl);
        setCurrentStreamTitle(reminder.channelName || reminder.programTitle);
        setPlayerOpen(true);
      }

      // Clear the session storage
      sessionStorage.removeItem("autoPlayReminder");
    };

    // Check for auto-play reminder on mount
    const savedReminder = sessionStorage.getItem("autoPlayReminder");

    if (savedReminder) {
      try {
        const reminder = JSON.parse(savedReminder);

        // Small delay to let the page load
        setTimeout(() => playChannel(reminder), 500);
      } catch (e) {
        console.error("Error parsing autoPlayReminder:", e);
        sessionStorage.removeItem("autoPlayReminder");
      }
    }

    // Listen for playReminderChannel event (when already on live-tv page)
    const handlePlayReminder = (event) => {
      const reminder = event.detail;

      playChannel(reminder);
    };

    window.addEventListener("playReminderChannel", handlePlayReminder);

    return () => {
      window.removeEventListener("playReminderChannel", handlePlayReminder);
    };
  }, []); // Only run on mount

  // Handle reminderFired event to remove from list
  useEffect(() => {
    const handleReminderFired = (event) => {
      const { id } = event.detail;

      debugLog("[LiveTV] Received reminderFired event for:", id);

      setReminders((prev) => {
        debugLog("[LiveTV] Filtering reminders, before:", prev.length);
        const filtered = prev.filter((r) => r.id !== id);

        debugLog("[LiveTV] After filter:", filtered.length);

        return filtered;
      });

      // Update channels if in reminders view (use ref to get current value)
      if (selectedCategoryRef.current === "reminders") {
        debugLog("[LiveTV] In reminders view, updating channels");
        setChannels((prev) => prev.filter((c) => c.uuid !== id));
      }
    };

    window.addEventListener("reminderFired", handleReminderFired);
    debugLog("[LiveTV] reminderFired listener attached");

    return () => {
      window.removeEventListener("reminderFired", handleReminderFired);
    };
  }, []); // Empty deps - uses ref for current category

  return (
    <div className="min-h-screen">
      {/* Left Menu */}
      <LeftMenu activeMenuItem="live" playlist={playlist} profile={profile} />

      {/* Mobile Menu */}
      <MobileMenu />

      <div className="ml-20 flex min-h-screen lg:h-screen">
        {/* Categories Sidebar */}
        <div className="epg-category-sidebar">
          <div className="epg-category-list">
            {categories.map((category) => (
              <button
                className={`epg-category-item ${
                  selectedCategory === category.id ? "active" : ""
                } ${category.isFixed ? "fixed-category" : ""} ${
                  enabledCategoryIds.includes(category.id)
                    ? "category-enabled"
                    : ""
                }`}
                data-category-id={category.id}
                key={category.id}
                onClick={() => {
                  // Only handle click if long press wasn't triggered
                  if (!longPressTriggeredRef.current) {
                    handleCategoryClick(category.id);
                  }
                  longPressTriggeredRef.current = false;
                }}
                onMouseDown={() => handleCategoryMouseDown(category)}
                onMouseLeave={handleCategoryMouseLeave}
                onMouseUp={handleCategoryMouseUp}
                onTouchEnd={handleCategoryMouseUp}
                onTouchStart={() => handleCategoryMouseDown(category)}
              >
                <div className="epg-category-content">
                  <span className="epg-category-name">
                    {category.icon &&
                      (category.icon.startsWith("fa-") ? (
                        <i className={`${category.icon} category-icon`} />
                      ) : (
                        <span className="category-icon mr-2">
                          {category.icon}
                        </span>
                      ))}
                    {category.name}
                  </span>
                  {(!category.isFixed ||
                    category.id === "favorites" ||
                    category.id === "reminders") &&
                    category.id !== "manage" &&
                    category.id !== "multiview" && (
                      <span className="epg-category-channels">
                        {category.count || 0}{" "}
                        {category.id === "reminders" ? "Reminders" : "Channels"}
                      </span>
                    )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* EPG Grid / Reminders List */}
        <div className="flex-1 relative">
          {loading ? (
            <div className="epg-loading">
              <div className="epg-loading-spinner" />
            </div>
          ) : selectedCategory === "reminders" ? (
            /* Reminders List View (Image 10) */
            <div className="p-6 overflow-y-auto h-full">
              {channels.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <span className="text-6xl mb-4">🔔</span>
                  <p className="text-gray-400 text-xl">No reminders set</p>
                  <p className="text-gray-500 text-sm mt-2">
                    Long press on an upcoming program to set a reminder
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {channels.map((reminder) => (
                    <div
                      className="flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all hover:scale-[1.02]"
                      key={reminder.uuid}
                      role="button"
                      style={{
                        backgroundColor: "rgba(25, 28, 35, 0.8)",
                      }}
                      tabIndex={0}
                      onClick={() => handleReminderClick(reminder)}
                      onKeyDown={activateOnEnterOrSpace}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(50, 55, 65, 0.9)";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          "rgba(25, 28, 35, 0.8)";
                      }}
                    >
                      {/* Program Image */}
                      <div
                        className="flex-shrink-0 rounded-lg overflow-hidden bg-gray-700"
                        style={{
                          width: "120px",
                          aspectRatio: "16 / 10",
                        }}
                      >
                        {reminder.image ? (
                          <img
                            alt={reminder.title}
                            className="w-full h-full object-cover"
                            src={reminder.image}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <i className="fas fa-tv text-2xl text-gray-500" />
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="text-white text-lg font-semibold">
                          {reminder.title}
                        </h3>
                        <p className="text-gray-400 text-sm">
                          {reminder.channelName}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-gray-500 text-sm">
                          <span className="flex items-center gap-1">
                            <i className="far fa-calendar text-purple-400" />
                            {formatDate(reminder.since)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="far fa-clock text-purple-400" />
                            {formatTime(reminder.since)} -{" "}
                            {formatTime(reminder.till)}
                          </span>
                        </div>
                      </div>

                      {/* Reminder Countdown Badge */}
                      <div className="flex-shrink-0 flex items-center">
                        <div
                          className="px-3 py-1.5 rounded-full text-xs font-medium"
                          style={{
                            background: "rgba(100, 149, 237, 0.2)",
                            color: "#6495ed",
                            border: "1px solid rgba(100, 149, 237, 0.3)",
                          }}
                        >
                          <i className="fas fa-bell mr-1.5" />
                          {(() => {
                            // Get reminder time from nested data
                            const data = reminder.reminderData || reminder;
                            const rt = data.reminderTime;
                            const reminderTime =
                              typeof rt === "string"
                                ? new Date(rt).getTime()
                                : rt;
                            // Use countdownTick for live updates
                            const now = countdownTick;
                            const diffMs = reminderTime - now;

                            if (diffMs <= 0) return "Reminder due";
                            const diffSecs = Math.floor(diffMs / 1000);
                            const diffMins = Math.floor(diffSecs / 60);
                            const diffHours = Math.floor(diffMins / 60);
                            const diffDays = Math.floor(diffHours / 24);

                            if (diffDays > 0) {
                              const remainingHours = diffHours % 24;

                              return remainingHours > 0
                                ? `Reminder in ${diffDays}d ${remainingHours}h`
                                : `Reminder in ${diffDays}d`;
                            } else if (diffHours > 0) {
                              const remainingMins = diffMins % 60;

                              return remainingMins > 0
                                ? `Reminder in ${diffHours}h ${remainingMins}m`
                                : `Reminder in ${diffHours}h`;
                            } else if (diffMins > 0) {
                              const remainingSecs = diffSecs % 60;

                              return remainingSecs > 0
                                ? `Reminder in ${diffMins}m ${remainingSecs}s`
                                : `Reminder in ${diffMins}m`;
                            } else {
                              return `Reminder in ${diffSecs}s`;
                            }
                          })()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <CustomEPG
              channels={channels}
              currentTime={currentTime}
              epgData={epgData}
              onChannelSelect={handleChannelSelect}
              onProgramLongPress={handleProgramLongPress}
              onProgramSelect={(program) => {
                if (!programLongPressTriggeredRef.current) {
                  // Check if this is a future program
                  const programStart = new Date(program.since).getTime();
                  const now = Date.now();

                  if (programStart > now) {
                    // Future program - show reminder overlay instead of playing
                    setSelectedProgramForReminder({
                      ...program,
                      channel: program.channel,
                      channelName: program.channel?.title,
                      channelLogo: program.channel?.logo,
                    });
                    setShowProgramDetails(true);
                  } else {
                    // Live or past program - play the channel
                    handleProgramSelect(program);
                  }
                }
                programLongPressTriggeredRef.current = false;
              }}
              onToggleFavorite={toggleFavorite}
            />
          )}
        </div>
      </div>

      {/* Dashboard Button */}
      <button
        className="fixed bottom-4 right-4 z-50 text-white text-xs sm:text-sm font-medium bg-black/50 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all duration-300"
        onClick={() => {
          // Clear all session data
          webPlayerStorage.clearAll();
          // Redirect to dashboard
          window.location.href = "https://dashboard.lunova.app";
        }}
      >
        Dashboard
      </button>

      {/* Manage Categories Overlay */}
      <ManageCategories
        categories={allCategories}
        isOpen={showManageCategories}
        profile={profile}
        onClose={() => setShowManageCategories(false)}
        onUpdate={(settings) => {
          debugLog("=== Category Update ===");

          // Store current visibility state before update
          const currentVisibility = {};

          categories.forEach((cat) => {
            if (!cat.isFixed) {
              // A category is currently visible if it's in the categories list
              currentVisibility[cat.id] = true;
            }
          });

          // Also check for hidden categories not in the current list
          allCategories.forEach((cat) => {
            if (!currentVisibility[cat.id]) {
              currentVisibility[cat.id] = false;
            }
          });

          debugLog("Current visibility (in sidebar):", currentVisibility);
          debugLog("New visibility settings:", settings.visible);

          // Track which categories changed visibility
          const changedToHidden = [];
          const changedToVisible = [];

          // Check each category's visibility change
          allCategories.forEach((cat) => {
            const currentlyVisible = currentVisibility[cat.id] || false;
            const willBeVisible = settings.visible[cat.id] || false;

            debugLog(
              `Category ${cat.id} (${cat.name}): currently=${currentlyVisible}, will be=${willBeVisible}`
            );

            // Track changes
            if (currentlyVisible && !willBeVisible) {
              changedToHidden.push(cat);
              debugLog(`🚫 Category "${cat.name}" (${cat.id}) will be HIDDEN`);
            } else if (!currentlyVisible && willBeVisible) {
              changedToVisible.push(cat);
              debugLog(`✅ Category "${cat.name}" (${cat.id}) will be SHOWN`);
            }
          });

          debugLog(
            "Final: Hidden=",
            changedToHidden.map((c) => c.id)
          );
          debugLog(
            "Final: Shown=",
            changedToVisible.map((c) => c.id)
          );

          // Show animations based on changes
          if (changedToHidden.length > 0) {
            const poofPositions = [];
            const hiddenIds = [];

            // Process all categories to hide
            changedToHidden.forEach((categoryToHide, index) => {
              debugLog(`🎆 Showing POOF animation for: ${categoryToHide.name}`);

              // Find the position of the category in the sidebar
              const categoryElement = document.querySelector(
                `.epg-category-item[data-category-id="${categoryToHide.id}"]`
              );

              if (categoryElement) {
                const rect = categoryElement.getBoundingClientRect();
                const position = {
                  id: categoryToHide.id,
                  top: rect.top + rect.height / 2,
                  left: rect.left + rect.width / 2,
                };

                debugLog(`Poof position for ${categoryToHide.name}:`, position);
                poofPositions.push(position);
                hiddenIds.push(categoryToHide.id);

                // Make the category invisible but keep its space
                categoryElement.style.transition = "all 0.3s ease";
                categoryElement.style.opacity = "0";
                categoryElement.style.pointerEvents = "none";

                // After a brief moment, start collapsing the space with stagger
                setTimeout(
                  () => {
                    categoryElement.style.transition =
                      "all 0.8s cubic-bezier(0.4, 0.0, 0.2, 1)";
                    categoryElement.style.maxHeight = `${categoryElement.offsetHeight}px`;
                    categoryElement.style.marginBottom = "8px";

                    // Force a reflow to ensure the maxHeight is set
                    void categoryElement.offsetHeight;

                    // Now collapse it
                    categoryElement.style.maxHeight = "0";
                    categoryElement.style.marginBottom = "0";
                    categoryElement.style.padding = "0";
                    categoryElement.style.border = "none";
                  },
                  300 + index * 100
                ); // Stagger the collapse
              }
            });

            // Show all poof animations
            setShowPoofAnimations(poofPositions);

            // Clear animations and remove categories after delay
            setTimeout(() => {
              debugLog("Animation complete, removing categories from list");
              setShowPoofAnimations([]);

              // Now remove the categories from the list
              setCategories((prevCategories) =>
                prevCategories.filter(
                  (cat) =>
                    !changedToHidden.some((hidden) => hidden.id === cat.id)
                )
              );
            }, 1500);
          } else if (changedToVisible.length > 0) {
            // Add newly visible categories
            const categoriesToShow = changedToVisible;

            debugLog(
              `✨ Showing categories:`,
              categoriesToShow.map((c) => c.name)
            );

            // Add the categories back to the visible list
            setCategories((prevCategories) => {
              const newCategories = [...prevCategories];

              categoriesToShow.forEach((categoryToShow) => {
                // Find where to insert based on saved order
                const savedOrder = localStorage.getItem("category_order");

                if (savedOrder) {
                  const order = JSON.parse(savedOrder);
                  const insertIndex = order.indexOf(categoryToShow.id);

                  if (insertIndex !== -1) {
                    // Find the correct position in the current list
                    let actualIndex = 3; // After fixed categories

                    for (let i = 0; i < insertIndex; i++) {
                      if (newCategories.some((cat) => cat.id === order[i])) {
                        actualIndex++;
                      }
                    }
                    newCategories.splice(actualIndex, 0, categoryToShow);
                  } else {
                    newCategories.push(categoryToShow);
                  }
                } else {
                  newCategories.push(categoryToShow);
                }
              });

              return newCategories;
            });

            // Apply animation to ALL shown categories
            setTimeout(() => {
              // Set all category IDs for animation
              const shownIds = categoriesToShow.map((cat) => cat.id);

              debugLog("Applying cornflower blue animation to:", shownIds);
              setEnabledCategoryIds(shownIds);

              // Scroll the first one into view
              if (categoriesToShow.length > 0) {
                const firstCategory = categoriesToShow[0];
                const categoryElement = document.querySelector(
                  `.epg-category-item[data-category-id="${firstCategory.id}"]`
                );

                if (categoryElement) {
                  categoryElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                }
              }

              // Clear animation after delay
              setTimeout(() => {
                setEnabledCategoryIds([]);
              }, 1000);
            }, 100);
          } else if (settings.orderChanged && settings.draggedCategory) {
            // A category was dragged and reordered - apply highlight animation
            debugLog(
              "Category was dragged and dropped:",
              settings.draggedCategory
            );

            // Apply the new order
            setCategories((prevCategories) => {
              const fixedCats = prevCategories.filter((cat) => cat.isFixed);
              const nonFixedCats = prevCategories.filter((cat) => !cat.isFixed);

              // Sort non-fixed categories according to the new order
              const orderedNonFixed = settings.order
                .map((id) => nonFixedCats.find((cat) => cat.id === id))
                .filter(Boolean);

              return [...fixedCats, ...orderedNonFixed];
            });

            // Apply cornflower blue highlight animation only to the dragged category
            setTimeout(() => {
              debugLog(
                "Applying cornflower blue animation to dragged category:",
                settings.draggedCategory
              );
              setEnabledCategoryIds([settings.draggedCategory]);

              // Clear animation after delay
              setTimeout(() => {
                setEnabledCategoryIds([]);
              }, 1000);
            }, 100);
          } else {
            // No visibility changes and no order changes
            debugLog("No changes detected");
          }
        }}
      />

      {/* Channel Sorting Overlay */}
      <AnimatePresence>
        {showChannelSorting && sortingCategory && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => {
              setShowChannelSorting(false);
              setSortingCategory(null);
            }}
          >
            {/* Backdrop with blur */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              }}
            />

            {/* Modal */}
            <motion.div
              animate={{ scale: 1, opacity: 1 }}
              className="relative z-10 w-[90%] max-w-[400px] rounded-2xl overflow-hidden"
              exit={{ scale: 0.95, opacity: 0 }}
              initial={{ scale: 0.95, opacity: 0 }}
              style={{
                backgroundColor: "rgba(55, 65, 81, 0.95)",
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-xl font-semibold text-white text-center">
                  Channel Sorting
                </h2>
              </div>

              {/* Divider */}
              <div
                className="mx-6"
                style={{
                  height: "1px",
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                }}
              />

              {/* Options */}
              <div className="py-2">
                {/* Adaptive Sort */}
                <button
                  className="w-full px-6 py-4 flex items-center gap-4 hover:bg-white/10 transition-colors"
                  onClick={() => handleSortOptionSelect("adaptive")}
                >
                  <i className="fa-solid fa-grid-2 text-blue-400 text-xl w-6" />
                  <span className="text-white text-lg flex-1 text-left">
                    Adaptive Sort
                  </span>
                  {getCurrentSortSetting(sortingCategory.id) === "adaptive" && (
                    <i className="fa-solid fa-check text-blue-400 text-lg" />
                  )}
                </button>

                {/* Divider */}
                <div
                  className="mx-6"
                  style={{
                    height: "1px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  }}
                />

                {/* Sort Alphabetically */}
                <button
                  className="w-full px-6 py-4 flex items-center gap-4 hover:bg-white/10 transition-colors"
                  onClick={() => handleSortOptionSelect("alphabetical")}
                >
                  <i className="fa-solid fa-arrow-down-a-z text-blue-400 text-xl w-6" />
                  <span className="text-white text-lg flex-1 text-left">
                    Sort Alphabetically
                  </span>
                  {getCurrentSortSetting(sortingCategory.id) ===
                    "alphabetical" && (
                    <i className="fa-solid fa-check text-blue-400 text-lg" />
                  )}
                </button>

                {/* Divider */}
                <div
                  className="mx-6"
                  style={{
                    height: "1px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  }}
                />

                {/* Reset Channels */}
                <button
                  className="w-full px-6 py-4 flex items-center gap-4 hover:bg-white/10 transition-colors"
                  onClick={() => handleSortOptionSelect("reset")}
                >
                  <i className="fa-solid fa-rotate-left text-blue-400 text-xl w-6" />
                  <span className="text-white text-lg flex-1 text-left">
                    Reset Channels
                  </span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Program Details Overlay (Image 7) */}
      <AnimatePresence>
        {showProgramDetails && selectedProgramForReminder && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => {
              setShowProgramDetails(false);
              setSelectedProgramForReminder(null);
            }}
          >
            {/* Backdrop with blur */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              }}
            />

            {/* Modal */}
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="relative z-10 w-[90%] max-w-[700px] rounded-2xl overflow-hidden"
              exit={{ opacity: 0, y: 20 }}
              initial={{ opacity: 0, y: 20 }}
              style={{
                backgroundColor: "rgba(40, 44, 52, 0.98)",
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Drag handle */}
              <div className="flex justify-center pt-3">
                <div className="w-10 h-1 bg-gray-500 rounded-full" />
              </div>

              {/* Content */}
              <div className="p-6">
                {/* Header with image and info */}
                <div className="flex gap-5">
                  {/* Program Image */}
                  <div
                    className="flex-shrink-0 rounded-lg overflow-hidden bg-gray-700"
                    style={{
                      width: "180px",
                      aspectRatio: "16 / 10",
                    }}
                  >
                    {selectedProgramForReminder.image ? (
                      <img
                        alt={selectedProgramForReminder.title}
                        className="w-full h-full object-cover"
                        src={selectedProgramForReminder.image}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <i className="fas fa-tv text-3xl text-gray-500" />
                      </div>
                    )}
                  </div>

                  {/* Program Info */}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-white">
                      {selectedProgramForReminder.title}
                    </h2>
                    <p className="text-gray-400 text-base mt-1">
                      {selectedProgramForReminder.channelName}
                    </p>
                    <p className="text-gray-500 text-sm mt-1">
                      {formatTime(selectedProgramForReminder.since)} -{" "}
                      {formatTime(selectedProgramForReminder.till)} •{" "}
                      {calculateDuration(
                        selectedProgramForReminder.since,
                        selectedProgramForReminder.till
                      )}
                    </p>
                  </div>
                </div>

                {/* Description */}
                {selectedProgramForReminder.description && (
                  <p className="text-gray-300 text-base mt-5 leading-relaxed">
                    {selectedProgramForReminder.description}
                  </p>
                )}

                {/* Divider */}
                <div
                  className="my-5"
                  style={{
                    height: "1px",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  }}
                />

                {/* Remind Me Button */}
                <button
                  className="w-full py-4 rounded-2xl text-white text-lg font-semibold transition-all duration-200 hover:scale-[1.02] hover:shadow-lg active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(100, 149, 237, 0.8)",
                  }}
                  onClick={handleRemindMeClick}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 1)";
                    e.currentTarget.style.boxShadow =
                      "0 0 20px rgba(100, 149, 237, 0.5)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Remind Me
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reminder Time Selection Overlay (Image 8) */}
      <AnimatePresence>
        {showReminderTimeSelect && selectedProgramForReminder && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => setShowReminderTimeSelect(false)}
          >
            {/* Backdrop with blur */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              }}
            />

            {/* Modal */}
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="relative z-10 w-[90%] max-w-[400px] rounded-2xl overflow-hidden p-6"
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
              style={{
                backgroundColor: "rgba(50, 54, 62, 0.98)",
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <h2 className="text-xl font-bold text-white mb-2">
                Set Reminder
              </h2>
              <p className="text-gray-400 text-base mb-6">
                When would you like to be reminded about{" "}
                {selectedProgramForReminder.title}?
              </p>

              {/* Time Options */}
              <div className="space-y-3">
                <button
                  className="w-full py-4 rounded-2xl text-white text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.8)",
                  }}
                  onClick={() => handleSetReminder(1)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.6)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(100, 149, 237, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  1 minute before
                </button>
                <button
                  className="w-full py-4 rounded-2xl text-white text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.8)",
                  }}
                  onClick={() => handleSetReminder(5)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.6)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(100, 149, 237, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  5 minutes before
                </button>
                <button
                  className="w-full py-4 rounded-2xl text-white text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.8)",
                  }}
                  onClick={() => handleSetReminder(15)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.6)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(100, 149, 237, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  15 minutes before
                </button>
                <button
                  className="w-full py-4 rounded-2xl text-white text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.8)",
                  }}
                  onClick={() => handleSetReminder(30)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.6)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(100, 149, 237, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  30 minutes before
                </button>
                <button
                  className="w-full py-4 rounded-2xl text-white/70 text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.5)",
                  }}
                  onClick={() => setShowReminderTimeSelect(false)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(90, 94, 102, 0.8)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.5)";
                  }}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cancel Reminder Dialog (Image 11) */}
      <AnimatePresence>
        {showCancelReminderDialog && reminderToCancel && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            onClick={() => {
              setShowCancelReminderDialog(false);
              setReminderToCancel(null);
            }}
          >
            {/* Backdrop with blur */}
            <div
              className="absolute inset-0"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(0, 0, 0, 0.7)",
              }}
            />

            {/* Modal */}
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="relative z-10 w-[90%] max-w-[400px] rounded-2xl overflow-hidden p-6"
              exit={{ opacity: 0, scale: 0.95 }}
              initial={{ opacity: 0, scale: 0.95 }}
              style={{
                backgroundColor: "rgba(55, 60, 70, 0.98)",
                boxShadow:
                  "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <h2 className="text-xl font-bold text-white mb-2">
                Cancel Reminder
              </h2>
              <p className="text-gray-400 text-base mb-6">
                Do you want to cancel the reminder for "
                {reminderToCancel.title ||
                  reminderToCancel.reminderData?.programTitle}
                "?
              </p>

              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  className="flex-1 py-4 rounded-2xl text-white text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "rgba(70, 74, 82, 0.8)",
                  }}
                  onClick={() => {
                    setShowCancelReminderDialog(false);
                    setReminderToCancel(null);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(100, 149, 237, 0.6)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(100, 149, 237, 0.3)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(70, 74, 82, 0.8)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  No
                </button>
                <button
                  className="flex-1 py-4 rounded-2xl text-lg font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                  style={{
                    backgroundColor: "transparent",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    color: "#f87171",
                  }}
                  onClick={confirmCancelReminder}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor =
                      "rgba(248, 113, 113, 0.2)";
                    e.currentTarget.style.borderColor =
                      "rgba(248, 113, 113, 0.5)";
                    e.currentTarget.style.boxShadow =
                      "0 0 15px rgba(248, 113, 113, 0.2)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                    e.currentTarget.style.borderColor =
                      "rgba(255, 255, 255, 0.2)";
                    e.currentTarget.style.boxShadow = "none";
                  }}
                >
                  Yes, Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Poof Animation Overlays - Multiple */}
      {showPoofAnimations.map((position, index) => (
        <div
          key={`poof-${position.id}-${index}`}
          style={{
            position: "fixed",
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translate(-50%, -50%)",
            zIndex: 99999,
            pointerEvents: "none",
            width: "200px",
            height: "200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: `fadeIn ${0.3 + index * 0.1}s ease`,
          }}
        >
          {poofAnimationData && (
            <Lottie
              height={200}
              isPaused={false}
              isStopped={false}
              options={{
                loop: false,
                autoplay: true,
                animationData: poofAnimationData.default || poofAnimationData,
                rendererSettings: {
                  preserveAspectRatio: "xMidYMid slice",
                },
              }}
              width={200}
            />
          )}
        </div>
      ))}

      {/* Video Player Modal */}
      {playerOpen && currentStreamUrl && (
        <PlyrVideoPlayer
          isOpen={playerOpen}
          poster={selectedChannel?.logo || selectedProgram?.channel?.logo}
          streamUrl={currentStreamUrl}
          title={currentStreamTitle}
          onClose={() => {
            setPlayerOpen(false);
            setCurrentStreamUrl(null);
            setCurrentStreamTitle(null);
          }}
        />
      )}
    </div>
  );
};

export default WebPlayerLiveTV;
